import { ASSET_CONFIG } from '@common/config';
import { PixeloramaJson, SpritesheetMetadataRegistry } from '@common/data/SpritesheetMetadata';
import { Assets, Rectangle, Texture } from 'pixi.js';

import { AssetManager } from '../AssetManager';

// ============================================================================

// ============================================================================
// Processed Spritesheet Data
// ============================================================================

/**
 * Processed animation data derived from a Pixelorama tag.
 */
export interface AnimationData {
  /** Animation name (from tag) */
  name: string;
  /** First frame in the spritesheet (1-based) */
  fromFrame: number;
  /** Last frame in the spritesheet (1-based, inclusive) */
  toFrame: number;
  /** Total number of frames in this animation */
  frameCount: number;
}

/**
 * Random duration range for a frame, parsed from Pixelorama user_data.
 */
export interface FrameDurationRange {
  /** Minimum duration multiplier */
  min: number;
  /** Maximum duration multiplier */
  max: number;
}

/**
 * Per-frame transform modifiers parsed from Pixelorama user_data.
 * Values represent the peak of a triangle wave (1.0 → target → 1.0) within the frame.
 * For position/rotation, the wave is (0 → target → 0).
 */
export interface FrameTransformModifier {
  /** Horizontal offset in pixels */
  x?: number;
  /** Vertical offset in pixels */
  y?: number;
  /**
   * Target scale multiplier for both axes.
   * 1.0 = no change, 2.0 = double, 0.5 = half.
   * Overridden by scaleX/scaleY if present.
   */
  scale?: number;
  /**
   * Target X scale multiplier.
   * 1.0 = no change, -1.0 = horizontal flip.
   */
  scaleX?: number;
  /**
   * Target Y scale multiplier.
   * 1.0 = no change, -1.0 = vertical flip.
   */
  scaleY?: number;
  /** Rotation in radians */
  rotation?: number;
}

/**
 * Processed spritesheet data ready for use by animation systems.
 */
export interface SpritesheetData {
  /** Spritesheet identifier (from export_file_name) */
  id: string;
  /** Animation frames per second */
  fps: number;
  /** Width of each frame in pixels */
  frameWidth: number;
  /** Height of each frame in pixels */
  frameHeight: number;
  /** Total number of frames in the spritesheet */
  totalFrames: number;
  /** Base frame duration in milliseconds (1000 / fps) */
  baseFrameDurationMs: number;
  /** Per-frame duration multipliers (0-indexed internally) */
  frameDurations: number[];
  /** Per-frame random duration ranges (0-indexed), undefined if no randomization */
  frameDurationRanges: (FrameDurationRange | undefined)[];
  /** Per-frame transform modifiers (0-indexed), undefined if no modifier */
  frameTransformModifiers: (FrameTransformModifier | undefined)[];
  /** Map of animation name to animation data */
  animations: Map<string, AnimationData>;
}

/**
 * Point data for sprite attachment points (HoldPoint, HeadPoint, etc.).
 * Contains per-frame coordinates indicating where on each frame an attachment should be placed.
 */
export interface PointData {
  /** Per-frame point coordinates. Index corresponds to frame number (0-indexed). */
  points: Array<{ x: number; y: number } | null>;
}

/** Single point coordinate for a frame */
export interface PointCoordinate {
  x: number;
  y: number;
}

// ============================================================================
// SpritesheetService
// ============================================================================

/** Default FPS fallback when spritesheet metadata is unavailable */
const DEFAULT_FPS_FALLBACK = 6;
const DEFAULT_FRAME_DURATION_MS = 1000 / DEFAULT_FPS_FALLBACK;

/**
 * Service for loading Pixelorama spritesheets and managing animation metadata.
 *
 * Responsibilities:
 * - Load JSON + PNG pairs from the sprites asset directory
 * - Parse Pixelorama format into SpritesheetData
 * - Create frame textures and register with AssetManager
 * - Provide lookup methods for animation systems
 */
export class SpritesheetService {
  private spritesheets: Map<string, SpritesheetData> = new Map();
  /** Generic points Map keyed by `${spritesheetId}:${pointType}` */
  private points: Map<string, PointData> = new Map();
  private loadingPromises: Map<string, Promise<SpritesheetData>> = new Map();
  private failedSpritesheets: Set<string> = new Set();
  private assetManager: AssetManager;
  private hotReloadTimestamp: number = 0;

  constructor(assetManager: AssetManager) {
    this.assetManager = assetManager;
  }

  /**
   * Clears internal caches to force re-fetching of all assets.
   * Typically called during a hot-reload triggered by the server.
   *
   * @param timestamp - Fresh timestamp to use for cache-busting
   */
  public clearCache(timestamp: number): void {
    this.spritesheets.clear();
    this.points.clear();
    this.loadingPromises.clear();
    this.failedSpritesheets.clear();
    this.hotReloadTimestamp = timestamp;
    console.log(`[SpritesheetService] Cache cleared. Hot-reload timestamp: ${timestamp}`);
  }

  /**
   * Load a spritesheet from its JSON and PNG files.
   * Files are expected at {SPRITES_URL_PATH}/{name}.json and {SPRITES_URL_PATH}/{name}.png
   *
   * @param name - Base name of the spritesheet files (without extension)
   */
  async loadSpritesheet(name: string): Promise<SpritesheetData> {
    // Check if already loaded
    const existing = this.spritesheets.get(name);
    if (existing) {
      return existing;
    }

    // Check if previously failed
    if (this.failedSpritesheets.has(name)) {
      throw new Error(`Spritesheet "${name}" has previously failed to load.`);
    }

    // Check if already loading
    const existingPromise = this.loadingPromises.get(name);
    if (existingPromise) {
      return existingPromise;
    }

    const loadPromise = (async () => {
      const timestampQuery = this.hotReloadTimestamp ? `?t=${this.hotReloadTimestamp}` : '';

      // Load JSON metadata
      const jsonPath = `${ASSET_CONFIG.SPRITES_URL_PATH}/${name}.json${timestampQuery}`;
      const response = await fetch(jsonPath);
      if (!response.ok) {
        throw new Error(
          `Failed to load spritesheet JSON: ${jsonPath} (Status: ${response.status})`,
        );
      }
      const json: PixeloramaJson = await response.json();

      // Load PNG texture
      const pngPath = `${ASSET_CONFIG.SPRITES_URL_PATH}/${name}.png${timestampQuery}`;
      const baseTexture: Texture = await Assets.load(pngPath);

      // Process into SpritesheetData
      const spritesheetData = this.processPixeloramaJson(json);

      // Create frame textures
      this.createFrameTextures(spritesheetData, baseTexture);

      // Warn if export_file_name doesn't match the filename (helpful for debugging)
      if (json.export_file_name !== name) {
        console.warn(
          `[SpritesheetService] export_file_name "${json.export_file_name}" doesn't match filename "${name}". Using "${name}" as the spritesheet ID.`,
        );
      }

      // Store by filename for consistency with loading check
      this.spritesheets.set(name, spritesheetData);
      this.loadingPromises.delete(name);

      // Try to load HoldPoint data (optional, non-blocking)
      void this.loadPoint(name, 'HoldPoint');

      return spritesheetData;
    })().catch((err) => {
      this.failedSpritesheets.add(name);
      this.loadingPromises.delete(name);
      throw err;
    });

    this.loadingPromises.set(name, loadPromise);
    return loadPromise;
  }

  /**
   * Process raw Pixelorama JSON into SpritesheetData.
   */
  private processPixeloramaJson(json: PixeloramaJson): SpritesheetData {
    const animations = new Map<string, AnimationData>();

    for (const tag of json.tags) {
      animations.set(tag.name, {
        name: tag.name,
        fromFrame: tag.from,
        toFrame: tag.to,
        frameCount: tag.to - tag.from + 1,
      });
    }

    const metadata: SpritesheetData = {
      id: json.export_file_name,
      fps: json.fps,
      frameWidth: json.size_x,
      frameHeight: json.size_y,
      totalFrames: json.frames.length,
      baseFrameDurationMs: 1000 / json.fps,
      frameDurations: json.frames.map((f) => f.duration),
      frameDurationRanges: json.frames.map((f) => this.parseFrameDurationRange(f.user_data)),
      frameTransformModifiers: json.frames.map((f) =>
        this.parseFrameTransformModifier(f.user_data),
      ),
      animations,
    };

    // Register metadata for common access
    SpritesheetMetadataRegistry.register(metadata.id, {
      width: metadata.frameWidth,
      height: metadata.frameHeight,
    });

    return metadata;
  }

  /**
   * Parse user_data string for duration range.
   * Expected format: "min:X; max:Y" where X and Y are numbers.
   */
  private parseFrameDurationRange(userData: string | undefined): FrameDurationRange | undefined {
    if (!userData) return undefined;
    const minMatch = userData.match(/min:\s*([\d.]+)/);
    const maxMatch = userData.match(/max:\s*([\d.]+)/);
    if (minMatch && maxMatch) {
      const min = parseFloat(minMatch[1]);
      const max = parseFloat(maxMatch[1]);

      // Validate: must be valid numbers, non-negative, and min <= max
      if (Number.isNaN(min) || Number.isNaN(max)) return undefined;
      if (min < 0 || max < 0) return undefined;
      if (min > max) return undefined;

      return { min, max };
    }
    return undefined;
  }

  /**
   * Parse user_data string for transform modifiers.
   * Expected format: "key: value; key: value" where keys are x, y, scale, rotation.
   * Example: "y: -5; scale: 0.1"
   */
  private parseFrameTransformModifier(
    userData: string | undefined,
  ): FrameTransformModifier | undefined {
    if (!userData) return undefined;

    const result: FrameTransformModifier = {};
    let hasAnyModifier = false;

    // Parse x offset
    const xMatch = userData.match(/\bx:\s*(-?[\d.]+)/);
    if (xMatch) {
      const x = parseFloat(xMatch[1]);
      if (!Number.isNaN(x)) {
        result.x = x;
        hasAnyModifier = true;
      }
    }

    // Parse y offset
    const yMatch = userData.match(/\by:\s*(-?[\d.]+)/);
    if (yMatch) {
      const y = parseFloat(yMatch[1]);
      if (!Number.isNaN(y)) {
        result.y = y;
        hasAnyModifier = true;
      }
    }

    // Parse scale (applies to both axes unless overridden)
    const scaleMatch = userData.match(/\bscale:\s*(-?[\d.]+)/);
    if (scaleMatch) {
      const scale = parseFloat(scaleMatch[1]);
      if (!Number.isNaN(scale)) {
        result.scale = scale;
        hasAnyModifier = true;
      }
    }

    // Parse scaleX (overrides scale for X axis)
    const scaleXMatch = userData.match(/\bscaleX:\s*(-?[\d.]+)/);
    if (scaleXMatch) {
      const scaleX = parseFloat(scaleXMatch[1]);
      if (!Number.isNaN(scaleX)) {
        result.scaleX = scaleX;
        hasAnyModifier = true;
      }
    }

    // Parse scaleY (overrides scale for Y axis)
    const scaleYMatch = userData.match(/\bscaleY:\s*(-?[\d.]+)/);
    if (scaleYMatch) {
      const scaleY = parseFloat(scaleYMatch[1]);
      if (!Number.isNaN(scaleY)) {
        result.scaleY = scaleY;
        hasAnyModifier = true;
      }
    }

    // Parse rotation (radians)
    const rotationMatch = userData.match(/\brotation:\s*(-?[\d.]+)/);
    if (rotationMatch) {
      const rotation = parseFloat(rotationMatch[1]);
      if (!Number.isNaN(rotation)) {
        result.rotation = rotation;
        hasAnyModifier = true;
      }
    }

    return hasAnyModifier ? result : undefined;
  }

  /**
   * Create individual frame textures and register them with AssetManager.
   * Textures are named: {spritesheetId}_{sheetFrame} (1-based frame number)
   */
  private createFrameTextures(data: SpritesheetData, baseTexture: Texture): void {
    for (let i = 0; i < data.totalFrames; i++) {
      const sheetFrame = i + 1; // Convert to 1-based
      const x = i * data.frameWidth;
      // Assumes single-row spritesheet layout (all frames in one horizontal row).
      // This matches Pixelorama's "Rows" export setting.
      const y = 0;

      const frameTexture = new Texture({
        source: baseTexture.source,
        frame: new Rectangle(x, y, data.frameWidth, data.frameHeight),
      });

      const textureId = `${data.id}_${sheetFrame}`;
      this.assetManager.registerTexture(textureId, frameTexture);
    }
  }

  /**
   * Get spritesheet data by ID.
   */
  getSpritesheet(id: string): SpritesheetData | undefined {
    return this.spritesheets.get(id);
  }

  /**
   * Get the default frame duration when spritesheet metadata is unavailable.
   */
  getDefaultFrameDurationMs(): number {
    return DEFAULT_FRAME_DURATION_MS;
  }

  /**
   * Check if a spritesheet is fully loaded.
   */
  isLoaded(id: string): boolean {
    return this.spritesheets.has(id);
  }

  /**
   * Check if a spritesheet has failed to load.
   */
  hasFailed(id: string): boolean {
    return this.failedSpritesheets.has(id);
  }

  /**
   * Get animation data for a specific animation within a spritesheet.
   */
  getAnimation(spritesheetId: string, animationName: string): AnimationData | undefined {
    const sheet = this.spritesheets.get(spritesheetId);
    if (!sheet) {
      return undefined;
    }
    return sheet.animations.get(animationName);
  }

  /**
   * Get the duration in milliseconds for a specific frame.
   *
   * @param spritesheetId - Spritesheet identifier
   * @param sheetFrame - 1-based global frame number
   * @returns Frame duration in ms, or base duration if sheet not found
   */
  getFrameDurationMs(spritesheetId: string, sheetFrame: number): number {
    const sheet = this.spritesheets.get(spritesheetId);
    if (!sheet) {
      return DEFAULT_FRAME_DURATION_MS;
    }
    // Validate frame bounds
    const totalFrames = sheet.frameDurations.length;
    if (sheetFrame < 1 || sheetFrame > totalFrames) {
      console.warn(
        `[SpritesheetService] sheetFrame ${sheetFrame} out of bounds for "${spritesheetId}" (1-${totalFrames}). Using default duration.`,
      );
      return DEFAULT_FRAME_DURATION_MS;
    }

    const frameIndex = sheetFrame - 1; // Convert to 0-based
    const durationMultiplier = sheet.frameDurations[frameIndex] ?? 1;
    return sheet.baseFrameDurationMs * durationMultiplier;
  }

  /**
   * Get the random duration range for a specific frame, if defined.
   *
   * @param spritesheetId - Spritesheet identifier
   * @param sheetFrame - 1-based global frame number
   * @returns FrameDurationRange if frame has randomization, undefined otherwise
   */
  getFrameDurationRange(spritesheetId: string, sheetFrame: number): FrameDurationRange | undefined {
    const sheet = this.spritesheets.get(spritesheetId);
    if (!sheet) return undefined;
    if (sheetFrame < 1 || sheetFrame > sheet.frameDurationRanges.length) return undefined;
    return sheet.frameDurationRanges[sheetFrame - 1];
  }

  /**
   * Get the transform modifier for a specific frame, if defined.
   *
   * @param spritesheetId - Spritesheet identifier
   * @param sheetFrame - 1-based global frame number
   * @returns FrameTransformModifier if frame has modifiers, undefined otherwise
   */
  getFrameTransformModifier(
    spritesheetId: string,
    sheetFrame: number,
  ): FrameTransformModifier | undefined {
    const sheet = this.spritesheets.get(spritesheetId);
    if (!sheet) return undefined;
    if (sheetFrame < 1 || sheetFrame > sheet.frameTransformModifiers.length) return undefined;
    return sheet.frameTransformModifiers[sheetFrame - 1];
  }

  /**
   * Convert animation-relative frame to global sheet frame.
   *
   * @param spritesheetId - Spritesheet identifier
   * @param animationName - Animation tag name
   * @param animationFrame - 1-based frame within the animation
   * @returns 1-based global sheet frame, or animationFrame if not found
   */
  animationFrameToSheetFrame(
    spritesheetId: string,
    animationName: string,
    animationFrame: number,
  ): number {
    const animation = this.getAnimation(spritesheetId, animationName);
    if (!animation) {
      return animationFrame;
    }
    return animation.fromFrame + animationFrame - 1;
  }

  /**
   * Get the texture ID for a specific sheet frame.
   *
   * @param spritesheetId - Spritesheet identifier
   * @param sheetFrame - 1-based global frame number
   * @returns Texture ID string for use with AssetManager
   */
  getTextureId(spritesheetId: string, sheetFrame: number): string {
    return `${spritesheetId}_${sheetFrame}`;
  }

  /**
   * Check if a spritesheet has a specific animation defined.
   */
  hasAnimation(spritesheetId: string, animationName: string): boolean {
    return this.getAnimation(spritesheetId, animationName) !== undefined;
  }

  /**
   * Get all animation names for a spritesheet.
   */
  getAnimationNames(spritesheetId: string): string[] {
    const sheet = this.spritesheets.get(spritesheetId);
    if (!sheet) {
      return [];
    }
    return Array.from(sheet.animations.keys());
  }

  /**
   * Batch-load multiple point types for a spritesheet.
   * Point JSON files are optional.
   *
   * @param spritesheetId - Spritesheet identifier
   * @param pointTypes - Array of point types (e.g., ['EyePoint', 'MouthPoint'])
   */
  public async loadPointsForSpritesheet(
    spritesheetId: string,
    pointTypes: string[],
  ): Promise<void> {
    await Promise.all(pointTypes.map((type) => this.loadPoint(spritesheetId, type)));
  }

  /**
   * Load point data for a spritesheet (if available).
   * Point JSON files are optional - spritesheets work without them.
   *
   * @param spritesheetId - Spritesheet identifier
   * @param pointType - Point type (e.g., 'HoldPoint', 'HeadPoint')
   */
  public async loadPoint(spritesheetId: string, pointType: string): Promise<void> {
    const timestampQuery = this.hotReloadTimestamp ? `?t=${this.hotReloadTimestamp}` : '';
    const jsonPath = `${ASSET_CONFIG.SPRITES_URL_PATH}/${spritesheetId}.${pointType}.json${timestampQuery}`;
    const key = `${spritesheetId}:${pointType}`;

    // Skip if already loaded
    if (this.points.has(key)) {
      return;
    }

    try {
      const response = await fetch(jsonPath);
      if (response.ok) {
        const data = (await response.json()) as PointData;
        this.points.set(key, data);
      }
      // Silently ignore 404 - Point data is optional
    } catch {
      // Ignore network errors for optional Point data
    }
  }

  /**
   * Get point data for a specific frame of a spritesheet.
   *
   * @param spritesheetId - Spritesheet identifier
   * @param pointType - Point type (e.g., 'HoldPoint', 'HeadPoint')
   * @param sheetFrame - 1-based frame number in the spritesheet
   * @returns Point coordinates or undefined if not available
   */
  getPointForFrame(
    spritesheetId: string,
    pointType: string,
    sheetFrame: number,
  ): PointCoordinate | undefined {
    const key = `${spritesheetId}:${pointType}`;
    const data = this.points.get(key);
    if (!data) return undefined;

    // Convert 1-based frame to 0-based index
    const frameIndex = sheetFrame - 1;
    if (frameIndex < 0 || frameIndex >= data.points.length) {
      return undefined;
    }

    const point = data.points[frameIndex];
    // Point can be null if no point was found in that frame
    return point ?? undefined;
  }

  /**
   * Get HoldPoint data for a specific frame of a spritesheet.
   * Convenience method - equivalent to getPointForFrame(spritesheetId, 'HoldPoint', sheetFrame).
   *
   * @param spritesheetId - Spritesheet identifier
   * @param sheetFrame - 1-based frame number
   * @returns HoldPoint coordinates or undefined if not available
   */
  getHoldPointForFrame(spritesheetId: string, sheetFrame: number): PointCoordinate | undefined {
    return this.getPointForFrame(spritesheetId, 'HoldPoint', sheetFrame);
  }

  /**
   * Get frame dimensions for a spritesheet.
   *
   * @param spritesheetId - Spritesheet identifier
   * @returns Frame width and height, or undefined if not loaded
   */
  getFrameSize(spritesheetId: string): { width: number; height: number } | undefined {
    const data = this.spritesheets.get(spritesheetId);
    if (!data) return undefined;
    return { width: data.frameWidth, height: data.frameHeight };
  }
}
